import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Video as VideoIcon, ShoppingBag, X, ExternalLink, Images, Upload, LayoutDashboard, Store, ClipboardList, Fingerprint } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import CarouselAdmin from "@/components/admin/CarouselAdmin";
import ShopAdmin from "@/components/admin/ShopAdmin";
import OrdersAdmin from "@/components/admin/OrdersAdmin";
import DashboardStats from "@/components/admin/DashboardStats";
import IdentitiesAdmin from "@/components/admin/IdentitiesAdmin";
import { toast } from "sonner";

const VIDEO_CATEGORIES = ["Enseignements", "Prophétie", "Louange", "Témoignages"];
const PRODUCT_CATEGORIES = ["Enseignements audio", "Livres et e-books", "Musique gospel", "Packs spirituels", "École Internationale des Chantres"];

const emptyVideo = { title: "", category: "Enseignements", duration: "", thumbnail: "", youtube_url: "" };
const emptyProduct = { name: "", description: "", price: "", category: "Enseignements audio", image: "", link: "", format: "", coming_soon: false };

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brown-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20 sticky top-0 bg-white">
          <h3 className="font-heading text-2xl font-medium text-brown-black">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-brown-black/50 hover:text-brown-black"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold mb-1.5">{label}</label>
    <input {...props} className="w-full border border-gold/30 rounded-lg px-3.5 py-2.5 focus:border-gold focus:outline-none transition-colors" />
  </div>
);

const Select = ({ label, options, ...props }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold mb-1.5">{label}</label>
    <select {...props} className="w-full border border-gold/30 rounded-lg px-3.5 py-2.5 focus:border-gold focus:outline-none transition-colors bg-white">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const AdminDashboard = () => {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [videos, setVideos] = useState([]);
  const [products, setProducts] = useState([]);

  const [videoModal, setVideoModal] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [videoForm, setVideoForm] = useState(emptyVideo);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [uploadingImg, setUploadingImg] = useState(false);

  const uploadProductImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/carousel/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProductForm((f) => ({ ...f, image: r.data.url }));
      toast.success("Miniature téléversée");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploadingImg(false); }
  };
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [loading, admin, navigate]);

  const load = useCallback(() => {
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNewVideo = () => { setVideoForm(emptyVideo); setEditId(null); setVideoModal(true); };
  const openEditVideo = (v) => { setVideoForm({ title: v.title, category: v.category, duration: v.duration, thumbnail: v.thumbnail, youtube_url: v.youtube_url }); setEditId(v.id); setVideoModal(true); };
  const openNewProduct = () => { setProductForm(emptyProduct); setEditId(null); setProductModal(true); };
  const openEditProduct = (p) => { setProductForm({ name: p.name, description: p.description, price: p.price, category: p.category, image: p.image, link: p.link || "", format: p.format || "", coming_soon: Boolean(p.coming_soon) }); setEditId(p.id); setProductModal(true); };

  const saveVideo = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/videos/${editId}`, videoForm);
      else await api.post("/videos", videoForm);
      toast.success(editId ? "Vidéo mise à jour" : "Vidéo ajoutée");
      setVideoModal(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...productForm, price: parseFloat(productForm.price) || 0 };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Produit mis à jour" : "Produit ajouté");
      setProductModal(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("Supprimer cette vidéo ?")) return;
    await api.delete(`/videos/${id}`); toast.success("Vidéo supprimée"); load();
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`); toast.success("Produit supprimé"); load();
  };

  if (loading || !admin) return <div className="min-h-screen flex items-center justify-center text-brown-black/50">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#FBF6E9]">
      <Seo title="Tableau de bord" description="Administration du ministère." path="/admin" noindex />
      {/* Header */}
      <header className="bg-brown-dark text-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <div>
            <p className="font-heading text-xl text-gold-light tracking-wide">Tableau de bord</p>
            <p className="text-xs text-ivory/50">{admin.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-sm text-ivory/70 hover:text-gold-light flex items-center gap-1.5" data-testid="admin-view-site">
              <ExternalLink size={15} /> Voir le site
            </button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/40 text-gold-light text-sm font-semibold hover:bg-gold hover:text-brown-dark transition-all duration-300">
              <LogOut size={15} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button onClick={() => setTab("dashboard")} data-testid="admin-tab-dashboard"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "dashboard" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <LayoutDashboard size={16} /> Tableau de bord
          </button>
          <button onClick={() => setTab("shop")} data-testid="admin-tab-shop"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "shop" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <Store size={16} /> Boutique
          </button>
          <button onClick={() => setTab("orders")} data-testid="admin-tab-orders"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "orders" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <ClipboardList size={16} /> Commandes
          </button>
          <button onClick={() => setTab("identities")} data-testid="admin-tab-identities"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "identities" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <Fingerprint size={16} /> Identités
          </button>
          <button onClick={() => setTab("videos")} data-testid="admin-tab-videos"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "videos" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <VideoIcon size={16} /> Vidéos ({videos.length})
          </button>
          <button onClick={() => setTab("products")} data-testid="admin-tab-products"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "products" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <ShoppingBag size={16} /> Contenus & EICE ({products.length})
          </button>
          <button onClick={() => setTab("carousel")} data-testid="admin-tab-carousel"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${tab === "carousel" ? "bg-gold text-white" : "bg-white text-brown-black/70 border border-gold/25"}`}>
            <Images size={16} /> Carrousel
          </button>
        </div>

        {tab === "dashboard" && <DashboardStats />}
        {tab === "shop" && <ShopAdmin />}
        {tab === "orders" && <OrdersAdmin />}
        {tab === "identities" && <IdentitiesAdmin />}

        {tab === "videos" && (
          <div>
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-heading text-2xl text-brown-black">Gestion des vidéos</h2>
              <button onClick={openNewVideo} data-testid="admin-add-video" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brown-dark text-ivory text-sm font-semibold hover:bg-brown-black transition-colors">
                <Plus size={16} /> Ajouter une vidéo
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {videos.map((v) => (
                <div key={v.id} className="bg-white rounded-xl overflow-hidden border border-gold/15" data-testid={`admin-video-${v.id}`}>
                  <img src={v.thumbnail} alt={v.title} className="w-full h-36 object-cover" />
                  <div className="p-4">
                    <span className="text-[11px] uppercase tracking-wide text-gold font-semibold">{v.category} · {v.duration}</span>
                    <h3 className="font-heading text-lg text-brown-black leading-snug mt-1">{v.title}</h3>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => openEditVideo(v)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gold/30 text-gold text-sm hover:bg-gold/10"><Pencil size={14} /> Modifier</button>
                      <button onClick={() => deleteVideo(v.id)} data-testid={`admin-delete-video-${v.id}`} className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "products" && (
          <div>
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-heading text-2xl text-brown-black">Gestion des produits</h2>
              <button onClick={openNewProduct} data-testid="admin-add-product" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brown-dark text-ivory text-sm font-semibold hover:bg-brown-black transition-colors">
                <Plus size={16} /> Ajouter un produit
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => (
                <div key={p.id} className="bg-white rounded-xl overflow-hidden border border-gold/15" data-testid={`admin-product-${p.id}`}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-full h-36 object-cover" />
                  ) : (
                    <div className="w-full h-36 bg-[#0b1436]/10 flex items-center justify-center text-brown-black/30"><ShoppingBag size={22} /></div>
                  )}
                  <div className="p-4">
                    <span className="text-[11px] uppercase tracking-wide text-gold font-semibold">{p.category}</span>
                    <h3 className="font-heading text-lg text-brown-black leading-snug mt-1">{p.name}</h3>
                    <p className="text-gold font-semibold mt-1">{Number(p.price).toFixed(2)} €</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => openEditProduct(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gold/30 text-gold text-sm hover:bg-gold/10"><Pencil size={14} /> Modifier</button>
                      <button onClick={() => deleteProduct(p.id)} data-testid={`admin-delete-product-${p.id}`} className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "carousel" && <CarouselAdmin />}
      </div>

      {/* Video modal */}
      <Modal open={videoModal} onClose={() => setVideoModal(false)} title={editId ? "Modifier la vidéo" : "Nouvelle vidéo"}>
        <form onSubmit={saveVideo} className="space-y-4">
          <Input label="Titre" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} required data-testid="video-form-title" />
          <Select label="Catégorie" options={VIDEO_CATEGORIES} value={videoForm.category} onChange={(e) => setVideoForm({ ...videoForm, category: e.target.value })} />
          <Input label="Durée (ex: 24:35)" value={videoForm.duration} onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })} required data-testid="video-form-duration" />
          <Input label="URL de la miniature" value={videoForm.thumbnail} onChange={(e) => setVideoForm({ ...videoForm, thumbnail: e.target.value })} data-testid="video-form-thumbnail" />
          <Input label="Lien YouTube" value={videoForm.youtube_url} onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })} data-testid="video-form-youtube" />
          <button type="submit" data-testid="video-form-submit" className="w-full py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors">Enregistrer</button>
        </form>
      </Modal>

      {/* Product modal */}
      <Modal open={productModal} onClose={() => setProductModal(false)} title={editId ? "Modifier le produit" : "Nouveau produit"}>
        <form onSubmit={saveProduct} className="space-y-4">
          <Input label="Nom" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required data-testid="product-form-name" />
          <Input label="Description" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} required data-testid="product-form-description" />
          <Input label="Prix (€)" type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required data-testid="product-form-price" />
          <Select label="Catégorie" options={PRODUCT_CATEGORIES} value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} />
          <div>
            <label className="block text-sm font-semibold text-brown-dark mb-1.5">Miniature (image)</label>
            <div className="flex items-center gap-3">
              {productForm.image && <img src={productForm.image} alt="aperçu" className="w-20 h-14 object-cover rounded-lg border border-gold/20" />}
              <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm font-semibold cursor-pointer hover:bg-gold/10 ${uploadingImg ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload size={15} /> {uploadingImg ? "Envoi…" : "Téléverser"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadProductImage(e.target.files?.[0])} data-testid="product-form-upload" />
              </label>
            </div>
            <input placeholder="ou URL de l'image" value={productForm.image} onChange={(e) => setProductForm({ ...productForm, image: e.target.value })} data-testid="product-form-image" className="w-full mt-2 border border-gold/30 rounded-lg px-3.5 py-2.5 focus:border-gold focus:outline-none transition-colors" />
          </div>
          <Input label="Badge format (ex: Livre, Ebook, Audio) — optionnel" value={productForm.format} onChange={(e) => setProductForm({ ...productForm, format: e.target.value })} data-testid="product-form-format" />
          <Input label="Lien d'achat externe (Amazon, etc.) — optionnel" value={productForm.link} onChange={(e) => setProductForm({ ...productForm, link: e.target.value })} data-testid="product-form-link" />
          <label className="flex items-center gap-2 text-sm text-brown-dark font-semibold cursor-pointer">
            <input type="checkbox" checked={productForm.coming_soon} onChange={(e) => setProductForm({ ...productForm, coming_soon: e.target.checked })} data-testid="product-form-coming-soon" className="w-4 h-4 accent-[#C9A13A]" />
            Arrive prochainement (produit non encore disponible)
          </label>
          <button type="submit" data-testid="product-form-submit" className="w-full py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
};

export default AdminDashboard;
